import parseNumber from "@tCentro/utilities/parseNumber";
import Color from "color";

const STATIC_PATH = "/userfiles/_static/store";

// -- Colors

const swatch = {
    white: "#ffffff",
    black: "#262525",
    lightGreen: "#00B900",
    alertMsgRed: "#ED0000",
    grey: {
        lighter: "#F6F6F6",
        light: "#F3F5F7",
        base: "#636161",
        dark: "#E0DFE0",
        darker: "#000000",
        shade: "#F1F6FB",
        grayShade: "#F2F2F2",
    },
};

export const COLORS = {
    white: swatch.white,
    black: swatch.black,
    lightGreen: swatch.lightGreen,
    alertMsgRed: swatch.alertMsgRed,
    lightBlack: "#353432",
    secondaryBtn: "#0D70B7",
    secondaryBg: "#EDF4F8",

    primary: {
        light: "#3CA865",
        base: "#266B40",
        dark: "#1B4D2E",
        contrast: "white",
    },
    secondary: {
        light: "#D10063",
        base: "#8F0446",
        dark: "#660332",
        contrast: "white",
    },
    tertiary: {
        light: "#676767",
        base: "#ED9C00",
        dark: "#676767",
        contrast: "black",
    },
    success: "#3CB56F",
    warning: "#E8AA0F",
    error: "#E23939",
    info: {
        base: "#3D3D3D",
    },
    background: {
        base: swatch.white,
        grayShade: swatch.grey.grayShade,
        alt: swatch.grey.lighter,
        hover: swatch.grey.light,
        tag: swatch.grey.base,
        shade: swatch.grey.shade,
    },
    border: {
        base: "#e2e3e6",
        dark: "#5d5f62",
        light: "#BDBBBD",
        darkShade: swatch.grey.dark,
        darker: swatch.grey.darker,
    },
    disabled: {
        lighter: "#e2e3e6",
        light: swatch.grey.light,
        base: "#939393",
        dark: swatch.grey.dark,
    },
    text: {
        lighter: swatch.grey.base,
        light: swatch.grey.dark,
        base: swatch.grey.darker,
        erpNumber: "#666",
        mineShaft: "#262525",
    },
    link: {
        base: "#0F96D9",
        hover: swatch.grey.dark,
    },
    shadow: {
        light: "rgba(48, 49, 51, 0.05)",
        base: "rgba(48, 49, 51, 0.1)",
    },
    pagination: {
        base: swatch.grey.light,
        hover: Color(swatch.grey.light).darken(0.15).hex(),
    },
    outline: "#1d8ed5",
};

// -- Typography

const fallbackFont = "Poppins";

export const TYPOGRAPHY = {
    fontFamily: {
        title: `'Poppins'`,
        base: `'Poppins'`,
    },
    weight: {
        base: 400,
        baseBold: 500,
        mediumbold: 600,
        bold: 700,
        black: 800,
    },
    size: {
        veryTiny: "10px",
        vTiny: "11px",
        tiny: "12px",
        smallest: "13px",
        smaller: "14px",
        small: "15px",
        base: "16px",
        large: "18px",
        larger: "20px",
        largest: "24px",
        xlargest: "26px",
        xlarge: "30px",
        xxlarge: "32px",
    },
    lineHeight: {
        smaller: 1,
        small: 1.25,
        base: "24px",
        large: 2,
    },
    spacing: {
        small: "0.5em",
        base: "1em",
        large: "1.5em",
        larger: "2em",
        largest: "3em",
    },
};

// -- Spacings
export const SPACING = {
    xxxlarge: "4em",
    xxlarge: "3em",
    xlarge: "2em",
    large: "1.5em",
    medium: "1.25em",
    standard: "1em",
    small: ".75em",
    xsmall: ".5em",
    xxsmall: ".25em",
    none: "0",
};

// -- Product

const productImageSizes = {
    small: {
        width: 311,
        height: 193,
    },
    medium: {
        width: 690,
        height: 429,
    },
    large: {
        width: 1164,
        height: 724,
    },
};

export const PRODUCT = {
    image: {
        ...productImageSizes,
        ratio: productImageSizes.large.height / productImageSizes.large.width,
    },
};

// -- Common

const contentWidth = 1710;

const spacing = {
    side: ["20px", "30px", "45px", "45px", "45px"],
    content: ["25px", "30px", "45px", "16px", "60px"],
    narrowContent: ["25px", "30px", "40px", "40px", "50px"],
};

const largestSideSpacing = spacing.side.filter(x => x).pop() as any;
const siteWidth = contentWidth + (parseNumber(largestSideSpacing) ?? 0) * 2;
// const siteWidth = 1440;

export const COMMON = {
    breakpoints: [0, 576, 768, 992, siteWidth],
    width: {
        content: contentWidth,
        site: siteWidth,
    },
    spacing,
    outline: {
        width: "2px",
        style: "solid",
    },
    defaultProductImagePath: `${STATIC_PATH}/product-not-available.svg`,
    defaultCategoryImagePath: `${STATIC_PATH}/category-not-available.svg`,
};

// -- Menu

export const MENU = {
    mobileButtonSpacing: ".25em",
    topNavItemSpacing: ".8em",
};

// -- Form

export const FORM = {
    field: {
        padding: "1em",
        spacing: "1em",
    },
    button: {
        spacing: "1.6em",
    },
};

// --Nisco Specific

export const NISCO = {
    COLORS: {
        darkGray: "#4A4A4A",
        lightGray: "#F5F5F5",
        borderGray: "#E6E6E6",
        boxShadow: "rgba(0, 0, 0, 0.1)",
        disabledGray: "#C4C4C4",
        lightBlue: "#F0F8FF",
        darkBlue: "#003366",
        mediumBlue: "#0F96D9",
        veryLightGray: "#FAFAFA",
        offWhite: "#F9F9F9",
        footerGray: "#2A2A2A",
        footerLightGray: "#4A4A4A",
        footerLinkGray: "#B2B2B2",
        footerBottomGray: "#1A1A1A",
        footerBottomLightGray: "#3A3A3A",
        headerGray: "#F7F7F7",
        headerBorderGray: "#E1E1E1",
        headerTextGray: "#6F6F6F",
        headerTextBlack: "#262525",
        headerTextBlue: "#0F96D9",
        headerTextLightBlue: "#0D70B7",
        orderHistoryLightGray: "#F3F5F7",
        orderHistoryBorderGray: "#E2E3E6",
        orderHistoryDarkGray: "#5D5F62",
        orderHistoryBlack: "#262525",
        orderHistoryBlue: "#0F96D9",
        orderHistoryLightBlue: "#0D70B7",
        cartLightGray: "#F9F9F9",
        cartBorderGray: "#E2E3E6",
        cartDarkGray: "#5D5F62",
        cartBlack: "#262525",
        cartBlue: "#0F96D9",
        cartLightBlue: "#0D70B7",
        cartGreen: "#3CB56F",
        cartRed: "#E23939",
        cartYellow: "#E8AA0F",
        cartOffWhite: "#FDFDFD",
        cartVeryLightGray: "#FAFAFA",
        cartDisabledGray: "#C4C4C4",
        cartTagGray: "#636161",
        productInfoGray: "#4A4A4A",
        productInfoLightGray: "#F5F5F5",
        productInfoBorderGray: "#E6E6E6",
        productInfoDisabledGray: "#C4C4C4",
        productInfoBoxShadow: "rgba(0, 0, 0, 0.1)",
        productInfoOffWhite: "#F9F9F9",
        productInfoVeryLightGray: "#FAFAFA",
        primaryColor: "#2B5197",
        primaryColorHover: "#1D3A6E",
        secondaryColor: "#0D70B7",
    },
    TYPOGRAPHY: {
        fontFamily: {
            title: `'Roboto', ${fallbackFont}`,
            base: `'Roboto', ${fallbackFont}`,
            secondaryFamiliy: `'Roboto Condensed', ${fallbackFont}`,
        },
        weight: {
            base: 400,
            baseBold: 500,
            mediumbold: 600,
            bold: 700,
            black: 800,
        },
        size: { 
            veryTiny: "10px",
            vTiny: "11px",
            tiny: "12px",
            smallest: "13px",
            smaller: "14px",
            small: "15px",
            base: "16px",
            large: "18px",
            larger: "20px",
            largest: "24px",
            xlargest: "26px",
            xlarge: "30px",
            xxlarge: "32px",
        },
        lineHeight: {
            smaller: 1,
            small: 1.25,
            base: "24px",
            large: 2,
        },
        spacing: {
            small: "0.5em",
            base: "1em",
            large: "1.5em",
            larger: "2em",
            largest: "3em",
        },
    },
};
